import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

import logger from '../../../../../../utils/logger';

const HEALTH_CONDITIONS = [
  "Heart Disease",
  "Cancer",
  "Diabetes",
  "Stroke",
  "Alzheimer's",
  "Respiratory Disease",
  "Kidney Disease",
  "Liver Disease",
  "Autoimmune Disorder",
  "Mental Health Condition",
];

const RELATIONSHIP_TYPES = [
  { value: "brother", label: "Brother" },
  { value: "sister", label: "Sister" },
];

const RecommendedAssessmentStep = ({
  data,
  previousData,
  member,
  onUpdate,
}) => {
  // Add debug log for props
  logger.info("RecommendedAssessmentStep PROPS:", {
    data,
    previousData,
    member,
  });

  const [formData, setFormData] = useState({
    fatherDob: "",
    fatherDod: "",
    fatherIsDeceased: false,
    motherDob: "",
    motherDod: "",
    motherIsDeceased: false,
    siblings: [],
    majorHealthConditions: [],
    predictedLifeExpectancy: 0,
    isValid: false,
    assessment_date: null,
  });
  const initializedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      // Handle both full ISO strings and date-only strings
      return dateString.includes("T") ? dateString.split("T")[0] : dateString;
    } catch (e) {
      logger.error("Error formatting date for display:", e);
      return "";
    }
  };

  // Format date for API
  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    try {
      // Ensure we return a proper ISO string
      const date = new Date(dateString);
      return date.toISOString();
    } catch (e) {
      logger.error("Error formatting date for API:", e);
      return null;
    }
  };

  // Initialize with existing data - with simpler effect
  useEffect(() => {
    if (!member || initializedRef.current) return;

    setIsLoading(true);

    try {
      // Only initialize if we have data
      if (data) {
        logger.info("Recommended Assessment Data Debug:", {
          raw_data: data,
          predicted_life_expectancy_rec: data.predicted_life_expectancy_rec,
          previousDataLifeExpectancy: previousData?.calculatedLifeExpectancy,
          member_data: member,
          recommended_assessment: member.recommendedAssessment,
          has_siblings_flag: data.has_siblings,
        });

        // Process health conditions to get just the condition names
        const healthConditions = Array.isArray(data.major_health_conditions)
          ? data.major_health_conditions.map((item) =>
              typeof item === "object" && item.condition
                ? item.condition
                : item,
            )
          : [];

        // Initialize siblings array from recommendedAssessment
        let siblingsData = [];

        if (
          data.siblings &&
          Array.isArray(data.siblings) &&
          data.siblings.length > 0
        ) {
          logger.info(
            "Using siblings from recommendedAssessment:",
            data.siblings,
          );

          siblingsData = data.siblings.map((sibling) => ({
            relationship: sibling.relationship,
            dateOfBirth: formatDateForDisplay(sibling.dateOfBirth),
            dateOfDeath: formatDateForDisplay(sibling.dateOfDeath),
            isDeceased: sibling.isDeceased,
            notes: sibling.notes || "",
          }));
        }

        // Log siblings for debugging
        logger.info("Siblings data processed:", {
          original_siblings: data.siblings || [],
          processed_siblings: siblingsData,
          has_siblings_flag: data.has_siblings,
        });

        // If the predictedLifeExpectancy is 0 or null, use the value from previous assessment
        const predictedLife =
          data.predicted_life_expectancy_rec ||
          previousData?.calculatedLifeExpectancy ||
          0;

        const newFormData = {
          fatherDob: formatDateForDisplay(data.father_dob),
          fatherDod: formatDateForDisplay(data.father_dod),
          fatherIsDeceased: Boolean(data.father_is_deceased),
          motherDob: formatDateForDisplay(data.mother_dob),
          motherDod: formatDateForDisplay(data.mother_dod),
          motherIsDeceased: Boolean(data.mother_is_deceased),
          siblings: siblingsData,
          majorHealthConditions: healthConditions,
          predictedLifeExpectancy: parseFloat(predictedLife),
          isValid: true,
          assessment_date: data.assessment_date || new Date().toISOString(),
        };

        logger.info("Setting form data:", {
          siblings: siblingsData,
          predicted_life_expectancy: predictedLife,
          form_data: newFormData,
        });

        setFormData(newFormData);
        initializedRef.current = true;
      } else if (previousData) {
        // Initialize with default values based on previous step
        const baseLifeExpectancy = previousData?.calculatedLifeExpectancy || 0;
        const newFormData = {
          fatherDob: "",
          fatherDod: "",
          fatherIsDeceased: false,
          motherDob: "",
          motherDod: "",
          motherIsDeceased: false,
          siblings: [],
          majorHealthConditions: [],
          predictedLifeExpectancy: parseFloat(baseLifeExpectancy),
          isValid: false,
          assessment_date: null,
        };

        setFormData(newFormData);
        initializedRef.current = true;
      }
    } catch (error) {
      logger.error("Error initializing recommended assessment data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [data, member, previousData]);

  // Update form validation and calculations when form data changes
  useEffect(() => {
    if (!initializedRef.current) return;

    const calculateLifeExpectancyAdjustment = () => {
      // Get base life expectancy from previous step
      let baseLifeExpectancy = previousData?.calculatedLifeExpectancy;
      if (!baseLifeExpectancy) {
        logger.error("No life expectancy value available from previous step");
        return 0;
      }

      let adjustment = 0;

      // Parent longevity adjustments
      if (
        formData.fatherIsDeceased &&
        formData.fatherDob &&
        formData.fatherDod
      ) {
        const fatherAge =
          new Date(formData.fatherDod).getFullYear() -
          new Date(formData.fatherDob).getFullYear();
        adjustment += fatherAge > 80 ? 1 : fatherAge < 65 ? -1 : 0;
      }

      if (
        formData.motherIsDeceased &&
        formData.motherDob &&
        formData.motherDod
      ) {
        const motherAge =
          new Date(formData.motherDod).getFullYear() -
          new Date(formData.motherDob).getFullYear();
        adjustment += motherAge > 80 ? 1 : motherAge < 65 ? -1 : 0;
      }

      // Health conditions adjustment
      adjustment -= Math.min(formData.majorHealthConditions.length, 3);

      // Sibling adjustment (add +1 for each sibling)
      if (formData.siblings && formData.siblings.length > 0) {
        adjustment += Math.min(formData.siblings.length, 2); // Max +2 for siblings
      }

      logger.info("Life Expectancy Calculation:", {
        base: baseLifeExpectancy,
        total_adjustment: adjustment,
        health_conditions: formData.majorHealthConditions,
        siblings: formData.siblings,
        final_value: baseLifeExpectancy + adjustment,
      });

      return baseLifeExpectancy + adjustment;
    };

    // Validate the form
    const validateForm = () => {
      // Check father's information
      const fatherValid = formData.fatherIsDeceased
        ? formData.fatherDob && formData.fatherDod
        : formData.fatherDob;

      // Check mother's information
      const motherValid = formData.motherIsDeceased
        ? formData.motherDob && formData.motherDod
        : formData.motherDob;

      // Require at least one parent's complete information
      return fatherValid || motherValid;
    };

    // Calculate new life expectancy and validate form
    const newLifeExpectancy = calculateLifeExpectancyAdjustment();
    const isValid = validateForm();

    // Always update when there are changes to health conditions or siblings
    const shouldUpdate = true;

    if (shouldUpdate) {
      // Update local form data
      setFormData((prev) => ({
        ...prev,
        predictedLifeExpectancy: newLifeExpectancy,
        isValid,
        assessment_date: isValid ? new Date().toISOString() : null,
      }));

      // Send update to parent
      const updatedData = {
        father_dob: formatDateForAPI(formData.fatherDob),
        father_dod: formatDateForAPI(formData.fatherDod),
        father_is_deceased: formData.fatherIsDeceased,
        mother_dob: formatDateForAPI(formData.motherDob),
        mother_dod: formatDateForAPI(formData.motherDod),
        mother_is_deceased: formData.motherIsDeceased,
        siblings: formData.siblings,
        major_health_conditions: formData.majorHealthConditions,
        predicted_life_expectancy_rec: newLifeExpectancy,
        isValid,
        assessment_date: isValid ? new Date().toISOString() : null,
      };

      onUpdate(updatedData);
    }
  }, [
    formData.fatherDob,
    formData.fatherDod,
    formData.fatherIsDeceased,
    formData.motherDob,
    formData.motherDod,
    formData.motherIsDeceased,
    formData.siblings,
    formData.majorHealthConditions,
    previousData?.calculatedLifeExpectancy,
    onUpdate,
  ]);

  // Simple form change handlers
  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleAddSibling = () => {
    setFormData((prev) => ({
      ...prev,
      siblings: [
        ...prev.siblings,
        {
          relationship: "brother", // Default to brother
          dateOfBirth: "",
          dateOfDeath: "",
          isDeceased: false,
          notes: "",
        },
      ],
    }));
  };

  const handleRemoveSibling = (index) => {
    setFormData((prev) => ({
      ...prev,
      siblings: prev.siblings.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateSibling = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      siblings: prev.siblings.map((sibling, i) =>
        i === index ? { ...sibling, [field]: value } : sibling,
      ),
    }));
  };

  const handleToggleHealthCondition = (condition) => {
    setFormData((prev) => {
      const currentConditions = Array.isArray(prev.majorHealthConditions)
        ? [...prev.majorHealthConditions]
        : [];

      // Check if the condition exists in the array (either as a string or an object with condition property)
      const existsAsString = currentConditions.includes(condition);
      const existsAsObject = currentConditions.some(
        (c) => typeof c === "object" && c.condition === condition,
      );

      const exists = existsAsString || existsAsObject;

      if (exists) {
        return {
          ...prev,
          majorHealthConditions: currentConditions.filter(
            (c) =>
              c !== condition &&
              (typeof c !== "object" || c.condition !== condition),
          ),
        };
      } else {
        return {
          ...prev,
          majorHealthConditions: [...currentConditions, condition],
        };
      }
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Recommended Health Assessment
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          fontWeight="bold"
          sx={{ display: "flex", alignItems: "center" }}
        >
          Family History
          <Tooltip title="Information about your immediate family's health history">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>

        <Grid container spacing={3}>
          {/* Father Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">Father</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Birth"
                  type="date"
                  value={formData.fatherDob}
                  onChange={handleInputChange("fatherDob")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={formData.fatherIsDeceased && !formData.fatherDob}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Death (if applicable)"
                  type="date"
                  value={formData.fatherDod}
                  onChange={handleInputChange("fatherDod")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={!formData.fatherIsDeceased}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.fatherIsDeceased}
                      onChange={handleCheckboxChange("fatherIsDeceased")}
                    />
                  }
                  label="Deceased"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Mother Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">Mother</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Birth"
                  type="date"
                  value={formData.motherDob}
                  onChange={handleInputChange("motherDob")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={formData.motherIsDeceased && !formData.motherDob}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Death (if applicable)"
                  type="date"
                  value={formData.motherDod}
                  onChange={handleInputChange("motherDod")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  disabled={!formData.motherIsDeceased}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.motherIsDeceased}
                      onChange={handleCheckboxChange("motherIsDeceased")}
                    />
                  }
                  label="Deceased"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Siblings */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="subtitle2">Siblings</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddSibling}
                size="small"
                variant="outlined"
              >
                Add Sibling
              </Button>
            </Box>

            {formData.siblings.length > 0 ? (
              formData.siblings.map((sibling, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="subtitle2">
                      Sibling {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSibling(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Relationship</InputLabel>
                        <Select
                          value={sibling.relationship}
                          onChange={(e) =>
                            handleUpdateSibling(
                              index,
                              "relationship",
                              e.target.value,
                            )
                          }
                          label="Relationship"
                        >
                          {RELATIONSHIP_TYPES.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Date of Birth"
                        type="date"
                        value={sibling.dateOfBirth}
                        onChange={(e) =>
                          handleUpdateSibling(
                            index,
                            "dateOfBirth",
                            e.target.value,
                          )
                        }
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Date of Death (if applicable)"
                        type="date"
                        value={sibling.dateOfDeath}
                        onChange={(e) =>
                          handleUpdateSibling(
                            index,
                            "dateOfDeath",
                            e.target.value,
                          )
                        }
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        disabled={!sibling.isDeceased}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sibling.isDeceased}
                            onChange={(e) =>
                              handleUpdateSibling(
                                index,
                                "isDeceased",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label="Deceased"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Health Notes"
                        value={sibling.notes}
                        onChange={(e) =>
                          handleUpdateSibling(index, "notes", e.target.value)
                        }
                        fullWidth
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No siblings added yet
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          fontWeight="bold"
          sx={{ display: "flex", alignItems: "center" }}
        >
          Health Conditions
          <Tooltip title="Check any conditions that run in your family">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>

        <Grid container spacing={2}>
          {HEALTH_CONDITIONS.map((condition) => {
            // Check if condition exists as either string or object
            const checked =
              formData.majorHealthConditions.includes(condition) ||
              formData.majorHealthConditions.some(
                (c) => typeof c === "object" && c.condition === condition,
              );

            return (
              <Grid item xs={12} sm={6} key={condition}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={() => handleToggleHealthCondition(condition)}
                    />
                  }
                  label={condition}
                />
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Paper
        sx={{
          p: 3,
          backgroundColor: "primary.light",
          color: "primary.contrastText",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Predicted Life Expectancy (Family History Adjusted)
        </Typography>
        <Typography variant="h3">
          {formData.predictedLifeExpectancy} years
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Based on your family history and health conditions
        </Typography>
      </Paper>
    </Box>
  );
};

export default RecommendedAssessmentStep;
